import { isDefined } from '@hover/utilities';

import type { ProductCatalogVariationFragment } from 'src/modules/homeownerApp/graphql/generated';
import { getProductProviderConfiguration } from 'src/modules/homeownerApp/helpers/productProviders';
import type { DesignerProduct } from 'src/modules/homeownerApp/productCatalogData';
import { toDesignerProducts } from 'src/modules/homeownerApp/productCatalogData';

import type {
  ProductCatalogProduct,
  ProductCatalogVariation,
  ProductCatalogVariationFacet,
} from './types';

export const flattenProducts = (
  products: ProductCatalogProduct[] | undefined,
) =>
  products?.flatMap(product => {
    if (!product?.variations) return [];
    return toDesignerProducts(product);
  }) ?? [];

export const flattenVariations = (
  variations: (ProductCatalogVariationFragment | null)[] | null | undefined,
) =>
  variations?.flatMap(variation => {
    if (!variation) return [];
    return toDesignerProducts(variation);
  }) ?? [];

const facetMatching =
  (name: string | undefined, fallback: boolean = false) =>
  (variationFacet: ProductCatalogVariationFacet) => {
    if (!name) return fallback;
    return variationFacet.facetValue?.facet.name === name;
  };

export const getPalettes = (
  providerUuid: string | undefined,
  variationFacets: ProductCatalogVariation['variationFacets'],
) => {
  const { paletteFacet } = getProductProviderConfiguration(providerUuid);

  // This logic will be going away once we implement faceted search. For now,
  // `paletteFacet` should always be used to filter `variationFacets` if it is
  // present. We check for a facet with the name of `'palette'`, in cases where
  // there are multiple variationFacets, we want to use the one for
  // `'palette'``. If there is no palette variationFacet, we use all of the
  // variationFacets.
  const palettesForFacetPalette =
    variationFacets?.filter(facetMatching(paletteFacet || 'palette')) || [];
  const allPalettes =
    variationFacets?.filter(facetMatching(paletteFacet, true)) || [];

  return (
    (palettesForFacetPalette?.length ? palettesForFacetPalette : allPalettes)
      .map(f => f.facetValue?.value)
      .filter(isDefined) ?? []
  );
};

export const filterOutInvalidPaints = (paint: DesignerProduct) =>
  paint.palettes.length > 0 && !!paint.color?.hex;

export const filterOutInvalidMaterials = (material: DesignerProduct) =>
  material.palettes.length > 0 &&
  !!material.variation.thumbnail &&
  !!material.prompt?.name;
